import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createOrganizationMembersQueryKey,
  createProfileQueryKey,
  isEventMessageRecipientsQueryKey,
} from '@/utils/queryKeys';
import type {
  RemoveOrganizationMemberMutation,
  RemoveOrganizationMemberMutationVariables,
} from './useRemoveOrganizationMember.generated';

const query = gql`
  mutation RemoveOrganizationMember($input: RemoveOrganizationMemberInput!) {
    removeOrganizationMember(input: $input)
  }
`;

export const useRemoveOrganizationMember = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    RemoveOrganizationMemberMutation,
    QueryError,
    RemoveOrganizationMemberMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        RemoveOrganizationMemberMutation,
        RemoveOrganizationMemberMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createOrganizationMembersQueryKey(organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: createProfileQueryKey(),
      });
      // A removed member can no longer receive texts; drop them from the event
      // modal's recipient picker.
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => isEventMessageRecipientsQueryKey(queryKey),
      });
    },
  });

  const removeMember = (
    userId: string,
    options?: MutateOptions<
      RemoveOrganizationMemberMutation,
      QueryError,
      RemoveOrganizationMemberMutationVariables
    >
  ) => mutate({ input: { organizationId, userId } }, options);

  return {
    removeMember,
    isPending,
  };
};
